import { useMemo, useState } from 'react';
import { Interval } from 'date-fns';
import { areDatesEqual, areIntervalsEqual } from '../../sharedComponents/dateHelper';
import { TransportOrder } from '../transportAssignment.types';
import {
    TransportOrderBulkUpdateDisplayState,
    TransportOrderBulkUpdateFormState,
} from './transportOrderBulkUpdateForm.types';
import { validateTransportOrderBulkUpdateForm } from './transportOrderBulkUpdateFormValidation';

const DEFAULT_TIMEZONE = 'UTC';

export const initialTransportOrderBulkUpdateFormState: TransportOrderBulkUpdateFormState = {
    truckLicensePlate: undefined,
    trailerLicensePlate: undefined,
    confirmedPickUpDate: undefined,
    confirmedDeliveryDate: undefined,
};

const initialTransportOrderBulkUpdateDisplayState: TransportOrderBulkUpdateDisplayState = {
    pickUpTimeZone: DEFAULT_TIMEZONE,
    deliveryTimeZone: DEFAULT_TIMEZONE,
    requestedPickUpDate: undefined,
    requestedDeliveryDate: undefined,
};

export const getUniqueValueOrUndefined = <T extends Date | string | undefined>(values: T[]): T | undefined => {
    if (values.length === 0) {
        return undefined;
    }

    const [firstValue, ...remainingValues] = values;

    return remainingValues.every((value) => areDatesEqual(firstValue, value)) ? firstValue : undefined;
};

const getUniqueIntervalOrUndefined = (values: (Interval | undefined)[]) => {
    if (values.length === 0) {
        return undefined;
    }

    const [firstValue, ...remainingValues] = values;

    return remainingValues.every((value) => areIntervalsEqual(firstValue, value)) ? firstValue : undefined;
};

export const getTimeZoneOrDefault = (timeZones: (string | undefined)[]) =>
    getUniqueValueOrUndefined(timeZones) ?? DEFAULT_TIMEZONE;

export const getInitialTransportOrderBulkUpdateFormState = (
    validAssignments: TransportOrder[],
): TransportOrderBulkUpdateFormState => {
    if (validAssignments.length === 0) {
        return { ...initialTransportOrderBulkUpdateFormState };
    }

    return {
        truckLicensePlate: getUniqueValueOrUndefined(
            validAssignments.map((assignment) => assignment.meansOfTransport?.id),
        ),
        trailerLicensePlate: getUniqueValueOrUndefined(
            validAssignments.map((assignment) => assignment.meansOfTransport?.trailerId),
        ),
        confirmedPickUpDate: getUniqueValueOrUndefined(
            validAssignments.map((assignment) => assignment.loading.confirmedPickUpDate),
        ),
        confirmedDeliveryDate: getUniqueValueOrUndefined(
            validAssignments.map((assignment) => assignment.unloading.confirmedDeliveryDate),
        ),
    };
};

export const getInitialTransportOrderBulkUpdateDisplayState = (
    validAssignments: TransportOrder[],
): TransportOrderBulkUpdateDisplayState => {
    if (validAssignments.length === 0) {
        return { ...initialTransportOrderBulkUpdateDisplayState };
    }

    const requestedPickUpDates = validAssignments.map((assignment) => assignment.loading.requestedPickUpDate);
    const requestedDeliveryDates = validAssignments.map((assignment) => assignment.unloading.requestedDeliveryDate);
    const pickUpTimeZones = validAssignments.map((assignment) => assignment.loading.timeZone);
    const deliveryTimeZones = validAssignments.map((assignment) => assignment.unloading.timeZone);

    return {
        pickUpTimeZone: getTimeZoneOrDefault(pickUpTimeZones),
        deliveryTimeZone: getTimeZoneOrDefault(deliveryTimeZones),
        requestedPickUpDate: getUniqueIntervalOrUndefined(requestedPickUpDates),
        requestedDeliveryDate: getUniqueValueOrUndefined(requestedDeliveryDates),
    };
};

export const useBulkUpdateConfirmationForm = (validAssignments: TransportOrder[]) => {
    const [formState, setFormState] = useState<TransportOrderBulkUpdateFormState>(() =>
        getInitialTransportOrderBulkUpdateFormState(validAssignments),
    );
    const displayState = useMemo(
        () => getInitialTransportOrderBulkUpdateDisplayState(validAssignments),
        [validAssignments],
    );
    const validation = useMemo(
        () => validateTransportOrderBulkUpdateForm(validAssignments, formState),
        [validAssignments, formState],
    );

    return {
        formState,
        displayState,
        validation,
        setMeansOfTransportId: (value: { id?: string; assetId?: string }) => {
            setFormState((currentFormState) => ({
                ...currentFormState,
                truckLicensePlate: value.id,
            }));
        },
        setTrailerId: (value: string | undefined) => {
            setFormState((currentFormState) => ({
                ...currentFormState,
                trailerLicensePlate: value,
            }));
        },
        setConfirmedPickUpDate: (value: Date | undefined) => {
            setFormState((currentFormState) => ({
                ...currentFormState,
                confirmedPickUpDate: value,
            }));
        },
        setConfirmedDeliveryDate: (value: Date | undefined) => {
            setFormState((currentFormState) => ({
                ...currentFormState,
                confirmedDeliveryDate: value,
            }));
        },
    };
};
